import React from "react";
import { useForm } from "react-hook-form";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Calendar, Check, Clock, Loader2, MessageCircle } from "lucide-react";
import SectionHeading from "@/components/site/SectionHeading";
import SEO from "@/components/site/SEO";
import { usePageContent } from "@/lib/usePageContent";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIME_WINDOWS = [
    "Morning (9 – 11 AM, your local time)",
    "Mid-day (11 AM – 1 PM)",
    "Early Afternoon (1 – 3 PM)",
    "Late Afternoon (3 – 5 PM)",
    "I'm flexible",
];

const Discovery = () => {
    const { get } = usePageContent("discovery");
    const {
        register,
        handleSubmit,
        setValue,
        watch,
        formState: { errors, isSubmitting },
    } = useForm();
    const preferred_time = watch("preferred_time");
    const [done, setDone] = React.useState(false);
    const [paymentState, setPaymentState] = React.useState({
        status: "idle",
        amount: null,
        currency: null,
        package_name: null,
    });

    React.useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const sessionId = params.get("session_id");
        const payment = params.get("payment");
        if (payment === "cancelled") {
            setPaymentState((s) => ({ ...s, status: "cancelled" }));
            toast.error("Payment cancelled. You can retry anytime.");
            window.history.replaceState({}, "", window.location.pathname);
            return;
        }
        if (!sessionId) return;
        setPaymentState((s) => ({ ...s, status: "polling" }));
        setDone(true);
        window.scrollTo({ top: 0, behavior: "smooth" });

        let attempts = 0;
        const maxAttempts = 6;
        const poll = async () => {
            try {
                const { data } = await axios.get(`${API}/checkout/status/${sessionId}`);
                if (data.payment_status === "paid") {
                    setPaymentState({
                        status: "paid",
                        amount: data.amount,
                        currency: data.currency,
                        package_name: data.package_name,
                    });
                    toast.success("Payment received. We'll be in touch.");
                    window.history.replaceState({}, "", window.location.pathname);
                    return;
                }
                if (data.status === "expired") {
                    setPaymentState((s) => ({ ...s, status: "failed" }));
                    return;
                }
                if (attempts < maxAttempts) {
                    attempts += 1;
                    setTimeout(poll, 2000);
                } else {
                    setPaymentState((s) => ({ ...s, status: "failed" }));
                }
            } catch (e) {
                setPaymentState((s) => ({ ...s, status: "failed" }));
            }
        };
        poll();
    }, []);

    const onSubmit = async (data) => {
        try {
            await axios.post(`${API}/discovery/request`, data);
            setDone(true);
            toast.success("Discovery Call request received.");
            window.scrollTo({ top: 0, behavior: "smooth" });
        } catch (e) {
            toast.error("Could not submit your request. Please try again.");
        }
    };

    return (
        <div data-testid="discovery-page">
            <SEO
                title="Discovery Call — complimentary 30-minute consultation"
                description="A 30-minute conversation to explore fit. No pitch, no slides — just a focused conversation about what your team needs."
                path="/discovery"
            />
            <section className="bg-indigo_brand text-white">
                <div className="ih-container py-20 md:py-24">
                    <p className="ih-eyebrow" style={{ color: "#08C020" }}>
                        {get("hero.eyebrow", "Discovery Call")}
                    </p>
                    <h1 className="mt-3 font-montserrat font-bold text-white text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
                        {get("hero.title", "A 30-minute conversation to explore fit.")}
                    </h1>
                    <p className="mt-7 font-opensans text-white/80 text-lg leading-relaxed max-w-2xl">
                        {get("hero.subtitle", "Tell us about your organization and what you're navigating. We'll confirm a time and come prepared.")}
                    </p>
                </div>
            </section>

            <section className="ih-section bg-surface">
                <div className="ih-container grid gap-10 lg:grid-cols-[1fr_1.4fr]">
                    <div className="lg:sticky lg:top-28">
                        <SectionHeading eyebrow="What to expect" title="A working conversation — not a sales pitch." />
                        <ul className="mt-8 space-y-5">
                            {[
                                { icon: MessageCircle, t: "We listen first", b: "What you&rsquo;re trying to achieve, what&rsquo;s in the way, and what success looks like." },
                                { icon: Clock, t: "30 focused minutes", b: "Scheduled at a window that fits your week — morning, mid-day, or afternoon." },
                                { icon: Calendar, t: "Clear next step", b: "If there&rsquo;s a fit, you&rsquo;ll leave with a concrete recommendation. If not, you&rsquo;ll leave with useful clarity." },
                            ].map(({ icon: Icon, t, b }) => (
                                <li key={t} className="flex gap-4">
                                    <span className="inline-flex w-11 h-11 items-center justify-center rounded-xl bg-green_brand/15 shrink-0">
                                        <Icon size={18} className="text-green_brand" />
                                    </span>
                                    <div>
                                        <p className="font-raleway font-semibold text-indigo_brand">{t}</p>
                                        <p className="font-opensans text-sm text-[#4A5568] mt-1 leading-relaxed" dangerouslySetInnerHTML={{ __html: b }} />
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {done ? (
                        <div data-testid="discovery-thanks" className="ih-card p-10 shadow-medium">
                            <span className="inline-flex w-12 h-12 items-center justify-center rounded-xl bg-green_brand/15">
                                <Check size={22} className="text-green_brand" />
                            </span>
                            {paymentState.status === "paid" ? (
                                <>
                                    <h3 className="mt-5 font-montserrat font-bold text-indigo_brand text-2xl" data-testid="disc-paid-headline">
                                        Payment received. Your call is confirmed.
                                    </h3>
                                    <p className="mt-3 font-opensans text-[#4A5568] text-base leading-relaxed">
                                        We&rsquo;ve received <strong className="text-indigo_brand">
                                        ${paymentState.amount ? paymentState.amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : ""} {paymentState.currency ? paymentState.currency.toUpperCase() : ""}
                                        </strong> for your {paymentState.package_name}. A confirmation receipt is on its way, and we&rsquo;ll follow up with a calendar invite shortly.
                                    </p>
                                </>
                            ) : paymentState.status === "polling" ? (
                                <>
                                    <h3 className="mt-5 font-montserrat font-bold text-indigo_brand text-2xl">
                                        Finalizing your payment&hellip;
                                    </h3>
                                    <p className="mt-3 font-opensans text-[#4A5568] text-base leading-relaxed">
                                        This usually takes a few seconds. Please don&rsquo;t close this window.
                                    </p>
                                </>
                            ) : paymentState.status === "failed" ? (
                                <>
                                    <h3 className="mt-5 font-montserrat font-bold text-indigo_brand text-2xl">
                                        We&rsquo;re double-checking your payment.
                                    </h3>
                                    <p className="mt-3 font-opensans text-[#4A5568] text-base leading-relaxed">
                                        If you were charged, you&rsquo;ll receive a confirmation email shortly. Contact us if you need help.
                                    </p>
                                </>
                            ) : (
                                <>
                                    <h3 className="mt-5 font-montserrat font-bold text-indigo_brand text-2xl">
                                        Got it.
                                    </h3>
                                    <p className="mt-3 font-opensans text-[#4A5568] text-base leading-relaxed">
                                        We&rsquo;ll confirm a time and send a calendar invite shortly. A confirmation has already gone to your inbox.
                                    </p>
                                </>
                            )}
                            <Link to="/" className="ih-btn-secondary mt-7" data-testid="disc-return-home">Return Home</Link>
                        </div>
                    ) : (
                        <form
                            onSubmit={handleSubmit(onSubmit)}
                            className="ih-card p-8 md:p-10 shadow-medium grid gap-5 sm:grid-cols-2"
                            data-testid="discovery-form"
                        >
                            <div style={{ display: "none" }} aria-hidden="true">
                                <input
                                    type="text"
                                    tabIndex={-1}
                                    autoComplete="off"
                                    {...register("hp_field")}
                                />
                            </div>
                            <Field label="First Name *" error={errors.first_name}>
                                <Input data-testid="disc-first_name" {...register("first_name", { required: true })} placeholder="Jordan" className="h-12" />
                            </Field>
                            <Field label="Last Name *" error={errors.last_name}>
                                <Input data-testid="disc-last_name" {...register("last_name", { required: true })} placeholder="Reyes" className="h-12" />
                            </Field>
                            <Field label="Work Email *" error={errors.email} className="sm:col-span-2">
                                <Input data-testid="disc-email" type="email" {...register("email", { required: true, pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/ })} placeholder="name@organization.com" className="h-12" />
                            </Field>
                            <Field label="Phone">
                                <Input data-testid="disc-phone" {...register("phone")} placeholder="+1 (555) 000-0000" className="h-12" />
                            </Field>
                            <Field label="Organization">
                                <Input data-testid="disc-organization_name" {...register("organization_name")} placeholder="Acme, Inc." className="h-12" />
                            </Field>
                            <Field label="Preferred Date">
                                <Input data-testid="disc-preferred_date" type="date" {...register("preferred_date")} className="h-12" />
                            </Field>
                            <Field label="Preferred Time Window">
                                <Select value={preferred_time} onValueChange={(v) => setValue("preferred_time", v)}>
                                    <SelectTrigger data-testid="disc-preferred_time" className="h-12"><SelectValue placeholder="Select a window" /></SelectTrigger>
                                    <SelectContent>{TIME_WINDOWS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                                </Select>
                                <input type="hidden" {...register("preferred_time")} />
                            </Field>
                            <Field label="What would you like to talk about?" className="sm:col-span-2">
                                <Textarea data-testid="disc-message" {...register("message")} rows={4} placeholder="A line or two about what you&rsquo;re navigating right now." />
                            </Field>

                            <div className="sm:col-span-2 pt-2 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
                                <p className="font-opensans text-xs text-[#4A5568]">Complimentary — no payment required. We&rsquo;ll respond within one business day.</p>
                                <button type="submit" disabled={isSubmitting} data-testid="disc-submit" className="ih-btn-primary disabled:opacity-60">
                                    {isSubmitting ? <><Loader2 className="animate-spin" size={16} /> Sending…</> : <>Request a Discovery Call <ArrowRight size={16} /></>}
                                </button>
                            </div>
                        </form>
                    )}
                </div>
            </section>
        </div>
    );
};

const Field = ({ label, error, children, className = "" }) => (
    <div className={className}>
        <Label className="font-raleway font-semibold text-indigo_brand text-sm">{label}</Label>
        <div className="mt-2">{children}</div>
        {error && <p className="mt-1 font-opensans text-xs text-red-600">This field is required.</p>}
    </div>
);

export default Discovery;
